import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * BatchPanel (005 US1 + 010 US1) — upload designs, pick one or MORE templates,
 * and enqueue ONE render queue covering templates × designs, watched in the Gallery.
 *
 * Multi-template (product-owner ask): templates reach the batch ONLY via
 * "Use this template for batch" (Templates/editor → `useSelectionStore.batchSelections`).
 * This panel renders those as removable chips and, on enqueue, fans the uploaded
 * designs across every selected template — each template becomes one DesignBatch,
 * all submitted to the shared QueueManager so they form a single export queue.
 *
 * Backward-compat: App still passes the single active selection via props (the
 * demo/offline seed). When no template has been explicitly queued we fall back to
 * that single selection, so the original one-template flow is unchanged.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useServices } from "../app/context.js";
import { useBatchStore } from "../stores/batch.js";
import { useSelectionStore } from "../stores/selection.js";
import { useCachedResource } from "../stores/useCachedResource.js";
import { CACHE_KEYS } from "../stores/sessionWarmup.js";
import { toDesignInputs } from "./upload.js";
// WS-O / D-36: this panel no longer calls `exportBundle` at all — the run writer owns every
// byte, from item 1. What it still reads from `export/bundle.js` is the shared WORDING
// (`destLabel`, `exportProblemSummary`) and the spill re-read (`hydrateOutputBytes`), so a copy
// and an FTP push cover the files on disk rather than whatever is still in RAM.
import {
  destLabel, exportProblemSummary, hydrateOutputBytes, runBundleName, MOCKUPS_BUNDLE_ROOT,
} from "../export/bundle.js";
// WS-O review-1 B-1: the run's refusal badge. Its own module because `export/bundle.js` is a
// pinned zero-diff file for this wave.
import { runProblemNotice } from "../export/runProblems.js";
import { copyRunTo } from "../export/copyRun.js";
import { getOutputBytesBudgetMb } from "../stores/memoryBudget.js";
import { getExportPrintFiles } from "../stores/exportPrintFiles.js";
import {
  probeSaveDir, saveDirBlocksRun, saveDirHealthLabel, saveDirHealthNote, type SaveDirHealth,
} from "./saveDirHealth.js";
import { pathSafe } from "../export/refs.js";
import { nextRunId } from "./runIds.js";
import { humanize } from "../templates/templateLabels.js";
import { readableName } from "../catalog/names.js";
import { inspectArtwork, validateArtworkFormat } from "./artwork.js";
import { Gallery } from "./Gallery.js";
import { MissingMockups, useMissingMockups } from "../mockups/MissingMockups.js";
import { DesignTree } from "./DesignTreeView.js";
import { Icon } from "../ui/Icon.js";
import { BusyButton } from "../ui/BusyButton.js";
import { RowAction } from "../ui/RowAction.js";
import { ActionBar } from "../ui/ActionBar.js";
import { getLocalOverrides, resolveWorkspaceSettings, resolvedOutputValues } from "../settings/localSettings.js";
import type {
  DesignBatch, ViewName, WorkspaceSettings, WorkspaceOutputSettings,
} from "@genz/platform";
import type { WireTemplateVersion } from "@genz/engine-bind";
import type { OutputRecord } from "@genz/platform";
import "./batch.css";

const RENDER_FORMATS = new Set(["jpg", "png", "webp"]);

/** `<Product Type>` folder — humanized product slug (e.g. "Transparent Phone Cases"). */
const mockupProductType = (productSlug: string): string => humanize(productSlug);
/**
 * `<Model>` folder of LAST resort — the humanized phone slug (e.g. "oneplus-6" → "Oneplus 6").
 *
 * Only the standalone/demo mount (which is handed a bare `props.context`) reaches this now:
 * every real selection carries `model` resolved from the catalog (`catalog/names`, F-7).
 *
 * Its `<Brand>` twin is GONE. It returned the leading token of the phone slug, and the slug
 * was `phoneModelId` — so an unresolved template wrote the id's own 8-char prefix as a folder
 * and nested the full id under it (`040d67ee/040d67ee 2b53 42b1 8ec3 B5fc1f6a2580`). A brand
 * is never guessed from a model string (`templateLabels.brandOf` has always refused to); when
 * it is unknown the segment is simply skipped, exactly as `buildRef` already handles.
 */
const mockupModel = (phoneSlug: string): string => humanize(phoneSlug);

/**
 * The template chip's subtitle — "OnePlus 6 · Clear TPU".
 *
 * It used to read `<phoneModel> · <templateId>`, and `phoneModel` WAS the `phoneModelId`, so
 * the Batch panel described every staged template with two bare UUIDs (F-7). Both halves are
 * now names resolved through `catalog/names`, and the template id — which support does need —
 * moves to the row's `title`. Either half is dropped when it is genuinely unknown rather than
 * printed as an empty segment.
 */
export function chipSubtitle(ctx: { phoneModel?: string; caseStyle?: string }): string {
  return [
    readableName(ctx.phoneModel, { noun: "Model" }),
    readableName(ctx.caseStyle, { noun: "Style" }),
  ]
    .map((part) => part.trim())
    .filter((part) => part && part !== "Model" && part !== "Style")
    .join(" · ");
}

/**
 * The run button's two labels, in ONE place (fix round 1, coordinator item 2).
 *
 * WS-Z3 renamed this button "Create Mock-up" → "Render" and the cancel message below kept
 * sending the operator to a control that is no longer on the surface. A message that names a
 * button and a button that names itself are the same fact written twice, so they are written
 * once: the label is read from here by the bar AND by every sentence that names it, and a
 * rename cannot leave one of them behind.
 */
export const RENDER_LABEL = "Render";
export const RENDER_BUSY_LABEL = "Rendering…";

/**
 * Flatten a relative output ref into ONE remote filename for the FTP transport, which
 * accepts a flat `{name, bytes}` list and creates no directories. Joining the segments
 * keeps every (template × view × design) distinct instead of collapsing them onto the
 * leaf name, which silently overwrote all but the last file on the server.
 */
export function ftpName(localRef: string): string {
  return localRef.split("/").filter(Boolean).join("__") || "output";
}

/**
 * One unique path segment per template in the run. The mockups tree keys on Product Type /
 * Brand / Model, which are properties of the DEVICE — two templates for the same model wrote
 * identical paths and overwrote each other. The template NAME disambiguates them; when two
 * SELECTED templates share a name, a suffix is appended so the scheme still guarantees one
 * folder per template.
 *
 * That suffix used to be the whole template id, so a duplicate name wrote
 * `Matte (a8097cc4-6cfb-41be-9799-0d320f1a7fd2)/` — F-7's own defect, re-entered through the
 * one branch that appends an id deliberately (review-2 item 3). The cascade is now:
 *
 *   1. the template's human CODE ("IM-16P-MAT") — the server keeps it scope-unique and the
 *      operator already reads it on every row;
 *   2. the 8-character head of the id, when a template has no code;
 *   3. the whole id, only if even the heads collide — a folder nobody can read beats two
 *      templates silently sharing one.
 *
 * It is deliberately NOT an ordinal ("Matte (2)"). An ordinal depends on selection ORDER, so
 * re-ordering or de-selecting the twin would re-label the SAME template and scatter its
 * output across two folders on the next run.
 */
export function templateSegments(
  targets: { templateId: string; name: string; code?: string | null }[],
): Map<string, string> {
  const counts = new Map<string, number>();
  for (const t of targets) {
    const label = pathSafe(t.name);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  // Which suffix each template would take, so step 3 can see whether step 1/2 was enough.
  // The id's head is taken AS WRITTEN (`slice`, not `shortId`): this is a label an operator
  // reads and quotes, so a `t-a` must stay `t-a` rather than lose its separator.
  const suffixOf = (t: { templateId: string; code?: string | null }) =>
    t.code?.trim() || t.templateId.slice(0, 8) || t.templateId;
  const suffixCounts = new Map<string, number>();
  for (const t of targets) {
    const key = `${pathSafe(t.name)}::${suffixOf(t)}`;
    suffixCounts.set(key, (suffixCounts.get(key) ?? 0) + 1);
  }
  const out = new Map<string, string>();
  for (const t of targets) {
    const label = pathSafe(t.name);
    if ((counts.get(label) ?? 0) <= 1) {
      out.set(t.templateId, label);
      continue;
    }
    const suffix = suffixOf(t);
    const unique = (suffixCounts.get(`${label}::${suffix}`) ?? 0) <= 1 ? suffix : t.templateId;
    out.set(t.templateId, `${label} (${pathSafe(unique)})`);
  }
  return out;
}

export interface BatchPanelProps {
  templateId: string;
  versionId: string;
  customerId: string;
  tenantId: string;
  /** Canonical wire template version (fetched from the cloud catalog/sync API); the
   * engine renders from its geometry — this wires the adapter into the render path. */
  templateVersion: WireTemplateVersion;
  /** Human label for the single (fallback) selection. */
  name?: string;
  /** Slug/display context for buildRef + the export sheet. */
  context: {
    customer: string;
    product: string;
    phoneSlug: string;
    caseStyleSlug: string;
    ext: "jpg" | "png" | "webp";
    /** Seller brand the Mockups funnel picked in step 4 — pre-selects the logo overlay. */
    sellerBrandId?: string;
  };
  /** Host hook: after a run is enqueued, the "View progress in Queue" affordance calls this
   *  so the workspace can switch to its live Queue tab. Absent in standalone/demo mounts. */
  onViewQueue?: () => void;
  /**
   * WS-P / AUD-8 LB-7: `TemplateSelection.bakeExpected` for the SINGLE-template path (this
   * panel reads a multi-template run off `batchSelections` instead, which already carries it).
   * `undefined` — a standalone/demo mount with no catalog row behind it — means "no opinion",
   * i.e. exactly today's behaviour.
   */
  bakeExpected?: boolean;
}

/** A template targeted by this batch run (resolved into the render path). */
interface BatchTarget {
  templateId: string;
  versionId: string;
  name: string;
  /** The template's human code — the folder-segment disambiguator when two staged templates
   *  share a name (review-2 item 3). Absent on the standalone/demo mount. */
  code?: string | null;
  templateVersion: WireTemplateVersion;
  context: BatchPanelProps["context"];
  /** This template's OWN saved views (from its version geometry). Each target renders
   *  exactly the views its template defines — not a single hard-coded default. */
  views: ViewName[];
  /** Real human folder labels from the Mockup Generator funnel (Phase 5). Present only
   *  when the funnel drove this selection; absent for the classic "Use for batch" entry,
   *  where the labels below are derived from the slugs (graceful fallback). */
  productType?: string;
  brand?: string;
  model?: string;
  /** WS-P: does this template's PUBLISHED version declare a baked mock-up? Forwarded to
   *  `EnqueueInput.bakeExpected`; `undefined` = no opinion (today's behaviour). */
  bakeExpected?: boolean;
}

/** The saved view list for a template version (its geometry's `view_type`s, in order).
 *  Falls back to `["back"]` only when the version defines no views. */
function viewsOf(version: WireTemplateVersion): ViewName[] {
  const vs = version.payload?.views?.map((v) => v.view_type).filter(Boolean) ?? [];
  return vs.length > 0 ? (vs as ViewName[]) : ["back"];
}

export function BatchPanel(props: BatchPanelProps) {
  const { client, io, queue, logger } = useServices();
  // WS-H2: re-read an output whose bytes were spilled after the auto-save wrote them, so the
  // gallery still shows a thumbnail for a file that is sitting in the output folder.
  const resolveSpilledBytes = useMemo(
    () => (o: OutputRecord) =>
      o.savedRoot && io.readExportedFile
        ? io.readExportedFile(o.savedRoot, o.localRef)
        : Promise.resolve(null),
    [io],
  );
  // Workspace output defaults (Settings) — cache-first, warmed at login. These DRIVE
  // the render encode (format + JPG/WebP quality + size), not just decoration.
  const settingsRes = useCachedResource<WorkspaceSettings>(CACHE_KEYS.settings, () => client.getSettings());
  const batch = useBatchStore();
  // Render-selection lives in the store so it survives sub-tab switches (Batch unmounts).
  const selectedDesignIds = useBatchStore((s) => s.selectedDesignIds);
  const setSelectedDesigns = useBatchStore((s) => s.setSelectedDesigns);
  const setSaveDir = useBatchStore((s) => s.setSaveDir);
  // Reactive read so the visible save-folder row updates the moment a folder is chosen.
  const saveDir = useBatchStore((s) => s.saveDir);
  const replacePreviousRun = useBatchStore((s) => s.replacePreviousRun);
  // Whole-run failure (swallowed engine throw, wired from the queue via bootstrap; or a
  // save-folder cancel) — shown as a dismissible banner that also releases the busy button.
  const jobError = useBatchStore((s) => s.jobError);
  const setJobError = useBatchStore((s) => s.setJobError);
  const selectedSet = useMemo(() => new Set(selectedDesignIds), [selectedDesignIds]);
  const batchSelections = useSelectionStore((s) => s.batchSelections);
  const removeFromBatch = useSelectionStore((s) => s.removeFromBatch);
  const clearBatch = useSelectionStore((s) => s.clearBatch);
  const [error, setError] = useState<string | null>(null);
  // Live folder-import progress (Task 7): a large design folder is read file-by-file, so we
  // surface a "done / total" row instead of a frozen-looking UI. null = not importing.
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  // Output preset lives in the STORE for the same reason the design selection does (see the
  // comment above): ExportWorkspace unmounts this panel on every sub-tab switch, so component
  // state was wiped by a trip to Queue and the next run silently used the workspace defaults.
  const presetName = useBatchStore((s) => s.outputPresetName); // "" = workspace default
  const setPresetName = useBatchStore((s) => s.setOutputPreset);
  const [ftpMsg, setFtpMsg] = useState<string | null>(null);
  const [ftpBusy, setFtpBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  /** WS-O / LB-2: the run folder, known from item 1 — what "Open folder" is enabled on. */
  const lastSavedPath = useBatchStore((s) => s.lastSavedPath);
  /** WS-O / O-8: the WEB run's staged zip — its count climbs live as the run fills. */
  const webZip = useBatchStore((s) => s.webZip);
  /** A failed reveal. Rendered as its own row, not appended to a status sentence. */
  const [openErr, setOpenErr] = useState<string | null>(null);
  /**
   * The folder "Open folder" opens — ONE answer for both controls that offer it, so the icon on
   * the save-folder row and the word in the action bar can never point at two different places.
   *
   * The RUN folder wins while there is one: mid-run that is the dated subfolder the writer is
   * filling, and after a run it is where the files actually are. Before any run there is only
   * the CHOSEN folder, and opening that is still the right answer to "open the folder" — it is
   * the one the row is naming.
   */
  const openTarget = lastSavedPath ?? saveDir;
  /** Clear the last failure, then try. The rejection is the row above, with its Retry. */
  const openFolder = (path: string): void => {
    setOpenErr(null);
    void io.openPath?.(path).catch((e: unknown) =>
      setOpenErr(e instanceof Error ? e.message : String(e)));
  };
  /**
   * WS-O review-1 B-1: what the RUN could not write — the count, the first path and its reason.
   *
   * The run writer has always had this (`RunWriter.report()`); until now nothing rendered it, so
   * a 1000-item run that went disk-full at item 200 read "1000/1000" with a full gallery, 200
   * files in the folder and no message at all. `app/bootstrap.ts::onOutput` publishes the tally
   * per item, so the number climbs while the run is going instead of arriving (or not) at the
   * end of it.
   */
  const runReport = useBatchStore((s) => s.runReport);
  /** WS-Y: does this device still export print files? Device-local and not exposed in Settings
   *  yet, so nothing in the app can flip it mid-session — read at render. */
  const exportsPrintFiles = getExportPrintFiles();
  // Designs are shown as a directory tree; a free-text filter narrows it.
  const [designQuery, setDesignQuery] = useState("");
  // Direction B: the Templates column is a dense PICK LIST, and a pick list you cannot search
  // is a scroll. Its own find field, mirroring the Designs column's — the two lists are the
  // same object twice over, so they carry the same affordance rather than one of them having it.
  const [templateQuery, setTemplateQuery] = useState("");
  // Device-local output overrides (Wave-2 T4): loaded once from LocalStore. They win over the
  // tenant/global workspace defaults in the SAME resolution the Settings panel shows.
  const [localOverrides, setLocalOverrides] = useState<Partial<WorkspaceOutputSettings>>({});
  useEffect(() => { void getLocalOverrides(io.localStore).then(setLocalOverrides); }, [io]);
  // Resolved effective output (local → tenant → global → default), WITH provenance: the
  // format's `source` decides whether it may override the template's own render_settings
  // (see the format precedence at enqueue). Flattened values drive quality/size.
  const resolvedSettings = useMemo(
    () => resolveWorkspaceSettings(settingsRes.data, localOverrides),
    [settingsRes.data, localOverrides],
  );
  const resolvedOutput = useMemo(() => resolvedOutputValues(resolvedSettings), [resolvedSettings]);
  const presets = resolvedOutput.output_presets;

  // Spec 017 US6: the approved brand whose logo is merged into each mockup at the template's
  // brand-logo placement is asked ONCE, on the Select tab (props.context.sellerBrandId). We
  // resolve its logo lazily at enqueue (see enqueue()), so there's no duplicate ask here.

  // Export the rendered outputs to the configured FTP server (desktop only; the cloud
  // never sees pixels — the desktop pushes bytes straight to the server).
  async function exportFtp() {
    setFtpMsg(null);
    setFtpBusy(true);
    try {
      const config = await client.getFtpConfig();
      if (!config.enabled) { setFtpMsg("FTP isn't enabled — configure it in Settings."); return; }
      // IMP-4: the filter used to be `o.bytes && o.bytes.length > 0`. After a spill a dropped
      // output is NOT empty — it is a file on disk with a `savedRoot` — so that predicate
      // shipped only what happened to still be in RAM. With write-through the spill is the
      // NORMAL state from item ~8 of any real run, so this was no longer a rare path: it was
      // the only one. Same predicate the copy verb uses, plus the same disk re-read.
      const uploadable = await hydrateOutputBytes(
        io,
        batch.outputs.filter((o) => (o.bytes?.length ?? 0) > 0 || o.savedRoot),
      );
      /**
       * review-1 I-3: a record whose re-read came back EMPTY is not uploadable — and it used to
       * be dropped right here, in silence, with `files.length` (the "Uploaded N files" total)
       * computed AFTER the filter. A 1000-item run whose folder lost 300 files uploaded 700 and
       * said "Uploaded 700 files to host." `hydrateOutputBytes` deliberately leaves such a
       * record untouched so the caller can report it; this is the caller doing that.
       */
      const unreadable = uploadable.filter((o) => !(o.bytes && o.bytes.length > 0));
      for (const o of unreadable) {
        logger?.log("warn", "Batch · output could not be re-read — not uploaded", {
          ref: o.localRef,
          savedRoot: o.savedRoot,
          recovery: "check the run folder is still attached, then export again",
        });
      }
      const shortBy = unreadable.length === 0 ? "" :
        ` ${unreadable.length} file${unreadable.length === 1 ? "" : "s"} could not be read back ` +
        `from the run folder and ${unreadable.length === 1 ? "was" : "were"} NOT uploaded — ` +
        `${unreadable[0].localRef}` +
        (unreadable.length > 1
          ? ` (+${unreadable.length - 1} more in Settings → Observability → Error log).`
          : ".");
      const files = uploadable
        .filter((o) => o.bytes && o.bytes.length > 0)
        // DATA LOSS: this used to send only the LEAF filename (`localRef.split("/").pop()`),
        // so every view of a design — and every template — collapsed onto ONE remote file
        // that each upload overwrote, and the run still reported success. The transport
        // takes a flat name list (no remote mkdir), so the relative path is preserved by
        // FLATTENING it: the whole ref becomes the filename with "/" → "__". Distinct refs
        // therefore stay distinct remote files.
        .map((o) => ({ name: ftpName(o.localRef), bytes: o.bytes! }));
      if (files.length === 0) {
        // …and when NOTHING could be read back, "create some mock-ups first" is a lie: the
        // mockups exist, the folder cannot give them back.
        setFtpMsg(unreadable.length > 0
          ? `Nothing was uploaded.${shortBy}`
          : "Nothing to upload yet — create some mock-ups first.");
        return;
      }
      const n = await io.ftpUpload(config, files);
      // Report what ACTUALLY landed, per file. The old message echoed the transport's count
      // as though it were the intended total, so a partial upload read as a full success.
      setFtpMsg(
        n >= files.length
          ? `Uploaded ${files.length} file${files.length === 1 ? "" : "s"} to ${config.host}.${shortBy}`
          : `Uploaded ${n} of ${files.length} files to ${config.host} — ${files.length - n} failed. Check the server and retry.${shortBy}`,
      );
    } catch (e) {
      logger?.log("error", "Batch · FTP export failed", { error: e });
      setFtpMsg(`FTP export failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setFtpBusy(false);
    }
  }

  // WS-O / D-36: `saveToFolder` is GONE. Its destination was `saveDir`, which is exactly where
  // the run writer has already written every mockup as it finished — so a same-destination
  // re-save had become a no-op that re-marshalled every byte across the IPC bridge. Its only
  // remaining use, a DIFFERENT destination, is this:

  /**
   * WS-O / AUD-8 LB-3: "Copy run to…" — the ONE re-export verb, and the only one that can
   * still be pressed by hand. It picks a NEW folder and copies the run's files there from
   * DISK, so a run whose bytes the budget dropped copies in full.
   */
  /**
   * WS-O / O-8: hand the WEB run's accumulated zip to the browser, on the operator's click.
   *
   * It hands over whatever is built at THAT MOMENT: a mid-run click produces a PARTIAL zip and
   * the status line says so, rather than a full-looking download that silently stops at item k.
   * `web.ts` is untouched — `io.exportBundle` already builds the zip (`createZip`) and triggers
   * the anchor download, and `createZip` is a one-shot builder whose contract this wave does
   * not change, so the entries are accumulated and compressed once, here, on the gesture.
   */
  async function downloadWebZip() {
    setSaveMsg(null);
    try {
      const zip = useBatchStore.getState().webZip;
      const files = zip?.entries() ?? [];
      if (files.length === 0) { setSaveMsg("Nothing to download yet — create some mock-ups first."); return; }
      const partial = rendering;
      await io.exportBundle({
        name: runBundleName("web", MOCKUPS_BUNDLE_ROOT, useBatchStore.getState().runFolder),
        files,
      });
      setSaveMsg(
        partial
          ? `Downloaded a PARTIAL zip — ${files.length} mockup${files.length === 1 ? "" : "s"} ` +
            "finished so far. The run is still going; download again when it ends for the rest."
          : `Downloaded ${files.length} mockup${files.length === 1 ? "" : "s"} as a zip.`,
      );
    } catch (e) {
      logger?.log("error", "Batch · zip download failed", { error: e });
      setSaveMsg(`Download failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function copyRunToFolder() {
    setSaveMsg(null);
    try {
      const srcRoot = useBatchStore.getState().lastSavedPath;
      if (!srcRoot) { setSaveMsg("Nothing to copy yet — this run hasn't written anything."); return; }
      if (!io.pickSaveDirectory) return;
      const destDir = await io.pickSaveDirectory();
      if (!destDir) { setSaveMsg("Copy cancelled — no folder chosen, nothing was written."); return; }
      const report = await copyRunTo(io, {
        srcRoot,
        destDir,
        outputs: useBatchStore.getState().outputs,
        printFiles: useBatchStore.getState().printFiles,
        designs: useBatchStore.getState().designs,
        logger, // write failures / path collisions belong in the Error log, not just here
      });
      setSaveMsg(
        `Copied ${report.written} file${report.written === 1 ? "" : "s"} ` +
        `${destLabel(report, io.kind, destDir)}.${exportProblemSummary(report)}`,
      );
    } catch (e) {
      logger?.log("error", "Batch · copy run failed", { error: e });
      setSaveMsg(`Copy failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // Result of a chooseSaveDir() call — a discriminated outcome instead of a shared mutable
  // ref. Each caller inspects ITS OWN call's result to decide what message to show, so a
  // genuine cancel from one call can never read stale state left behind by another
  // (previously a `saveDirPickErrored` ref was written by chooseSaveDir() and read by
  // enqueue(); two concurrent calls could interleave those writes/reads and a real cancel
  // would silently show no message at all).
  type SaveDirPick = { dir: string } | { cancelled: true } | { error: true };

  // Reentrancy guard: while a dialog is already in flight, a second concurrent call (e.g.
  // the Choose… button clicked while enqueue() is also opening one) is a no-op instead of
  // racing to open two native dialogs or stomping saveDir with whichever resolves last.
  const saveDirPickInFlight = useRef(false);

  /**
   * WS-O / LB-2 (1): the folder's health, probed at MOUNT (for a remembered folder) and again
   * at `enqueue()` before the run arms — never per render and never per item. Counted so a
   * regression to "probe on every render" is a test failure rather than a thousand IPC calls.
   */
  const [saveDirHealth, setSaveDirHealth] = useState<SaveDirHealth>("unknown");
  const probeCount = useRef(0);
  const checkSaveDir = async (dir: string | undefined): Promise<SaveDirHealth> => {
    probeCount.current += 1;
    (globalThis as { __genzSaveDirProbes?: number }).__genzSaveDirProbes = probeCount.current;
    const health = await probeSaveDir(io, dir);
    if (health === "unauthorized" || health === "missing") {
      // A refusal the operator can act on must reach the Error log too, not only the row.
      logger?.log("warn", `Batch · save folder ${health} — the run cannot write there`, {
        dir, health, recovery: "pick the folder again (Change…) before starting a run",
      });
    }
    return health;
  };
  // ONE probe at mount, and only when there IS a remembered folder to probe.
  useEffect(() => {
    let live = true;
    void (async () => {
      const dir = useBatchStore.getState().saveDir;
      if (!dir) return;
      const health = await checkSaveDir(dir);
      if (live) setSaveDirHealth(health);
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [io]);

  /** Desktop-only: open the native folder picker. Hardened against a rejected/throwing
   *  dialog (previously an unhandled promise that silently aborted the render) AND against
   *  overlapping calls. Returns a discriminated result: `{ dir }` on success, `{ cancelled:
   *  true }` on cancel/unsupported (`io.pickSaveDirectory` missing)/reentrant no-op, or
   *  `{ error: true }` when the dialog itself threw (a message was already shown). */
  const chooseSaveDir = async (): Promise<SaveDirPick> => {
    if (saveDirPickInFlight.current) {
      logger?.breadcrumb("action", "Batch · save-folder dialog already in flight, ignoring");
      return { cancelled: true };
    }
    if (!io.pickSaveDirectory) return { cancelled: true };
    saveDirPickInFlight.current = true;
    logger?.breadcrumb("action", "Batch · opening save-folder dialog");
    try {
      const dir = await io.pickSaveDirectory();
      if (!dir) {
        logger?.breadcrumb("action", "Batch · save-folder dialog cancelled");
        return { cancelled: true };
      }
      setSaveDir(dir);
      logger?.breadcrumb("action", "Batch · save folder chosen", { dir });
      // A freshly PICKED folder is authorized by the dialog itself, so the badge clears. The
      // probe still runs, because "picked" and "reachable" are not the same thing on a drive
      // that is being unplugged as we speak.
      setSaveDirHealth(await checkSaveDir(dir));
      return { dir };
    } catch (e) {
      logger?.log("error", "Batch · save-folder dialog failed", { error: e });
      setSaveMsg(`Couldn't open the folder dialog: ${e instanceof Error ? e.message : String(e)}`);
      return { error: true };
    } finally {
      saveDirPickInFlight.current = false;
    }
  };

  // Targets = explicitly queued templates, else the single (demo/props) fallback.
  const targets: BatchTarget[] = useMemo(() => {
    if (batchSelections.length > 0) {
      return batchSelections.map((s) => ({
        templateId: s.templateId,
        versionId: s.versionId,
        name: s.name,
        code: s.code,
        templateVersion: s.templateVersion,
        context: {
          customer: s.context.customer,
          product: s.context.product,
          phoneSlug: s.context.phoneSlug,
          caseStyleSlug: s.context.caseStyleSlug,
          ext: s.context.ext,
          // The funnel's seller-brand pick MUST ride along per target. Dropping it here was
          // the last link in the "mockups render with no logo" chain: enqueue() then had only
          // props.context (== batchSelections[0]) to look at, so every other template in the
          // run lost its logo — and a re-ordered selection lost it entirely.
          sellerBrandId: s.context.sellerBrandId,
        },
        views: viewsOf(s.templateVersion),
        // Carry the funnel's real labels through when present (Phase 5).
        productType: s.context.productType,
        brand: s.context.brand,
        model: s.context.model,
        bakeExpected: s.bakeExpected,
      }));
    }
    return [
      {
        templateId: props.templateId,
        versionId: props.versionId,
        name: props.name ?? "Selected template",
        templateVersion: props.templateVersion,
        context: props.context,
        views: viewsOf(props.templateVersion),
        bakeExpected: props.bakeExpected,
      },
    ];
  }, [batchSelections, props.templateId, props.versionId, props.name, props.templateVersion, props.context, props.bakeExpected]);

  const pickOpts = {
    accept: ["image/png", "image/jpeg", "image/webp", "image/gif", "image/bmp", "image/tiff"],
    multiple: true,
    maxBytes: 32 * 1024 * 1024,
  };

  const ingest = async (pick: () => Promise<Awaited<ReturnType<typeof io.pickFiles>>>) => {
    setError(null);
    try {
      const files = await pick();
      if (files.length === 0) return;
      // Artwork validation (spec 018 §6): reject oversize/unsupported/corrupt artwork
      // BEFORE it enters the render queue, so no generation cost is consumed. Where the
      // decoder is available (web/desktop) we check real dimensions; otherwise format only.
      const canDecode = typeof (io as { decodeImage?: unknown }).decodeImage === "function";
      const accepted: typeof files = [];
      const rejected: string[] = [];
      for (const f of files) {
        const v = canDecode
          ? await inspectArtwork(io as { decodeImage: (b: Uint8Array) => Promise<{ width: number; height: number }> }, f)
          : validateArtworkFormat(f.name);
        if (v.ok) accepted.push(f);
        else rejected.push(v.message);
      }
      if (rejected.length > 0) {
        setError(`Skipped ${rejected.length} file(s). ${rejected[0]}`);
      }
      if (accepted.length === 0) return;
      // toDesignInputs derives each design's category from its source subfolder (spec 014).
      // "+ Add" APPENDS: it used to call setDesigns(), which replaced the whole list AND
      // re-ticked every design the operator had deliberately unticked. addDesigns() keeps the
      // existing designs and their tick state, and drops files already staged (same name+path).
      const added = batch.addDesigns(toDesignInputs(accepted));
      // Mirror the primary target onto the batch store (single-flow compatibility).
      batch.setTemplate(targets[0].templateId, targets[0].versionId, targets[0].views);
      logger?.breadcrumb("action", `Batch · imported ${added} designs`, { count: added });
    } catch (e) {
      logger?.log("error", "Batch · design import failed", { error: e });
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      // Clear the folder-import progress row on success, cancel (empty), or error.
      setImportProgress(null);
    }
  };

  const upload = () => ingest(() => io.pickFiles(pickOpts));
  // Folder import: keeps the Designs/<Category>/ structure as output categories (spec 014).
  // A big folder is read file-by-file, so surface live progress instead of freezing.
  const uploadFolder = () =>
    ingest(() => io.pickDirectory({ ...pickOpts, onProgress: (done, total) => setImportProgress({ done, total }) }));
  const importing = importProgress !== null;

  // Tree: a checkbox marks a design (or every design under a folder) to RENDER.
  const onTreeToggle = (ids: string[], on: boolean) => {
    const next = new Set(selectedDesignIds);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    setSelectedDesigns([...next]);
  };
  // ✕ on a file/folder drops those designs from the staged batch entirely.
  const onTreeRemove = (ids: string[]) => {
    const drop = new Set(ids);
    batch.setDesigns(batch.designs.filter((d) => !drop.has(d.designId)));
    // `setDesigns` is the FRESH-IMPORT action: it re-ticks everything. Removing one file is
    // not a fresh import, so restore the operator's ticks (minus the removed ids) — otherwise
    // deleting one design silently re-armed every design they had unticked.
    setSelectedDesigns(selectedDesignIds.filter((id) => !drop.has(id)));
  };

  // Only the SELECTED designs render; the render count/summary follows the selection.
  const designsToRender = useMemo(
    () => batch.designs.filter((d) => selectedSet.has(d.designId)),
    [batch.designs, selectedSet],
  );

  // Prevent duplicate registration: the render is async, so without a guard each
  // rapid click enqueued a fresh job. `pending` covers the click→first-progress
  // gap; `rendering` (derived from batch progress) keeps the button disabled until
  // the in-flight batch finishes.
  const [pending, setPending] = useState(false);
  // Held true for the WHOLE enqueue() async span — from the click, through the save-folder
  // dialog AND the lazy listBrands seller-logo fetch, until enqueue completes or fails. This
  // makes the Render button busy IMMEDIATELY (before any awaited work) and, unlike
  // `pending`, is never released by the 3s pending safety guard: a slow (>3s) listBrands can
  // no longer let the button re-enable while jobs are still un-enqueued.
  const [enqueueing, setEnqueueing] = useState(false);
  // Set once a run is enqueued this session → reveals the "View progress in Queue" hand-off
  // to the workspace's live Queue tab. Never set on the save-folder cancel early-return.
  const [enqueued, setEnqueued] = useState(false);
  /** Set when the run's output size had to be reduced to the workspace's max-canvas ceiling
   *  — the operator is getting a smaller image than the preset promises, so it is stated. */
  const [sizeCapped, setSizeCapped] = useState<
    { wantW: number; wantH: number; width: number; height: number; maxCanvas: number } | null
  >(null);
  /** Set when the run asked for a seller brand's logo and could not get one — the whole run
   *  exports UNBRANDED, and a breadcrumb was the only record of it. Same class as
   *  `sizeCapped`: the operator asked for one thing and is receiving another. */
  const [brandLogoMissing, setBrandLogoMissing] = useState<string | null>(null);
  /**
   * H2-7 / review-1 M-3: the once-per-RUN latch for the web over-budget warn.
   *
   * WS-O: the warn moved out of the deleted auto-save effect and is now driven by the outputs
   * themselves, because there is no end-of-run write left to hang it off. Same sentence, same
   * context, same once-per-run rule — the latch re-arms when a run arms, i.e. when
   * `clearOutputs()` empties the store.
   */
  const overBudgetWarnedRef = useRef(false);
  const { completed, total } = batch.progress;
  const rendering = total > 0 && completed < total;
  // `rendering` (aggregate progress vs expectedTotal) is authoritative for in-flight work.
  // A `jobError` overrides only the pre-render `pending` latch — NOT `rendering`: in a
  // multi-template run (one job per target) one job's wholesale failure must not release the
  // button while sibling jobs are still rendering, or a re-click's clearOutputs() would wipe
  // their completed outputs. The failed job is accounted for via `failJob` (its expected
  // items leave `expectedTotal`), so `rendering` settles to false once no job is still
  // active — including the single-job case, where the only job's failure zeroes the total.
  const busy = enqueueing || (!jobError && pending) || rendering;

  // When a run fails/cancels, drop the click→first-progress guard so the button re-enables.
  useEffect(() => {
    if (jobError && pending) setPending(false);
  }, [jobError, pending]);

  useEffect(() => {
    if (!pending) return;
    if (rendering || (total > 0 && completed >= total)) {
      setPending(false);
      return;
    }
    // Safety: clear the guard if no progress ever arrives (e.g. an early failure).
    const t = setTimeout(() => setPending(false), 3000);
    return () => clearTimeout(t);
  }, [pending, rendering, total, completed]);

  /**
   * WS-O / D-36: the end-of-run auto-save is GONE.
   *
   * It was the ONLY thing that wrote files, and it could not run until the last item had
   * rendered — AUD-8 probe B measured a 20-item run reaching "20/20 (UI: FINISHED)" with ZERO
   * files on disk, the first byte landing 18 ms AFTER the run ended and 172 ms after item 1.
   * Scaled to 500 designs × 2 views that is ~7 minutes of an empty folder, and total loss of
   * every rendered pixel on a sleep, a crash or a quit at minute 6.
   *
   * Every byte it used to write is now written by `export/runWriter.ts` the instant its item
   * finishes (`runBatch.onItem` → `QueueManager.onOutput` → one `RunWriter` per run), and its
   * spill call moved onto that writer's `onWritten`. There is no second writer left to
   * disagree with the first, and no `autoSavedRun` latch left to guard a save that no longer
   * exists.
   *
   * What survives here is the WEB over-budget warn, verbatim: web cannot spill (the zip goes to
   * the browser's download, not to a folder this app can read back), so holding the bytes is
   * still the lesser evil — and the operator must still be TOLD when a run walks past the
   * ceiling, or the only signal is the Export tab dying. One warn per run, naming the budget
   * and what is actually held.
   */
  useEffect(() => {
    // A fresh run arms with an empty store (`clearOutputs`) — that is the latch's reset, and it
    // is what makes "a NEW run may warn again" true without warning on every render tick.
    if (batch.outputs.length === 0 && batch.printFiles.length === 0) {
      overBudgetWarnedRef.current = false;
      return;
    }
    if (io.kind === "desktop" || overBudgetWarnedRef.current) return;
    {
      const budgetMb = getOutputBytesBudgetMb();
      const s = useBatchStore.getState();
      const held =
        s.outputs.reduce((n, o) => n + (o.bytes?.byteLength ?? 0), 0) +
        s.printFiles.reduce((n, p) => n + (p.bytes?.byteLength ?? 0), 0);
      if (held > budgetMb * 1024 * 1024) {
        overBudgetWarnedRef.current = true;
        logger?.log(
          "warn",
          "batch: output bytes over budget — web keeps them in memory (nothing to re-read from)",
          {
            heldMb: Math.round(held / 1024 / 1024),
            budgetMb,
            outputs: s.outputs.length,
            printFiles: s.printFiles.length,
          },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch.outputs, batch.printFiles, io]);

  const designCount = batch.designs.length; // staged (all imported)
  const designCountSel = designsToRender.length; // selected → will render
  const jobCount = targets.length * (designCountSel > 0 ? 1 : 0);

  const enqueue = async () => {
    logger?.breadcrumb("action", "Batch · render requested", {
      targets: targets.length,
      designs: designCountSel,
      saveDir: Boolean(useBatchStore.getState().saveDir),
    });
    if (busy || designCountSel === 0 || targets.length === 0) return; // ignore repeat clicks while in flight
    // Show busy IMMEDIATELY — before the save-folder dialog OR the lazy listBrands fetch — and
    // hold it (via `enqueueing`, folded into `busy`) until this whole span completes or fails.
    // The `finally` below is the single reset point covering every early return.
    setEnqueueing(true);
    try {
    // Arm a fresh run: clear any prior failure/cancel banner so it can't linger over a new run.
    setJobError(null);
    // Ask for the save folder BEFORE rendering (desktop). Cancel aborts the run so no
    // partial state is created. The chosen folder is remembered for later runs.
    if (io.kind === "desktop" && !useBatchStore.getState().saveDir && io.pickSaveDirectory) {
      const result = await chooseSaveDir();
      if (!("dir" in result)) {
        // Don't stomp a more specific "Couldn't open the folder dialog…" message that
        // chooseSaveDir already surfaced (in THIS call's own result) when the dialog itself
        // failed — a plain cancel is promoted to the unmissable error banner instead.
        if (!("error" in result)) {
          setJobError(`Cancelled — no save folder chosen. Click ${RENDER_LABEL} to pick one.`);
        }
        return; // cancelled/failed → don't render (finally clears `enqueueing`)
      }
    }
    // WS-O / LB-2 (1): with write-through the folder is a PRECONDITION of the run, so the
    // "can we actually write there?" question is asked HERE — before `clearOutputs()` arms
    // anything — instead of being discovered at the first refused write and recovered with a
    // modal nobody is watching. `unknown` never blocks: a platform without the probe and a
    // probe that itself failed both fall through to the old behaviour.
    if (io.kind === "desktop") {
      const health = await checkSaveDir(useBatchStore.getState().saveDir);
      setSaveDirHealth(health);
      if (saveDirBlocksRun(health)) {
        setJobError(
          `${saveDirHealthLabel(health)!.label} — ${saveDirHealthNote(health)} ` +
          "Nothing was rendered.",
        );
        return; // don't arm the run (finally clears `enqueueing`)
      }
    }
    setPending(true);
    // One queue covering templates × designs: clear the gallery so accumulated outputs
    // across this run start clean — but KEEP designs/selection/saveDir (no reset), then
    // declare the run's total item count so the Gallery shows AGGREGATE progress.
    batch.clearOutputs();
    // Only designs that carry image bytes actually render (runBatch filters byte-less ones
    // out at run.ts:62). Counting the byte-less designs in expectedTotal used to leave the
    // run short of its total → the button hung on "Rendering…" forever. So the run total
    // (and the enqueued job) uses the SAME bytes-filtered predicate, and each skipped
    // byte-less design is reported as a failure row up front so it's not silently dropped.
    const renderableDesigns = designsToRender.filter((d) => d.bytes && d.bytes.length > 0);
    const byteLessDesigns = designsToRender.filter((d) => !(d.bytes && d.bytes.length > 0));
    // Each target renders ITS OWN views, so the run total sums per-target
    // (renderable designs × that template's view count) — not a single shared view count.
    // The skipped byte-less designs are appended as failure ROWS below, and those rows count
    // toward `outputs + failures`; they MUST therefore also count toward `expectedTotal`.
    // Leaving them out made the run look finished the instant they landed, which released the
    // Render button MID-RUN — and a re-click's clearOutputs() wiped the live job's outputs.
    batch.setExpectedTotal(
      targets.reduce((sum, t) => sum + renderableDesigns.length * t.views.length, 0) +
        byteLessDesigns.length,
    );
    if (byteLessDesigns.length > 0) {
      const failView = targets[0].views[0] ?? ("back" as ViewName);
      batch.appendResult({
        batchId: nextRunId("batch-skipped"),
        outputs: [],
        printFiles: [],
        failures: byteLessDesigns.map((d) => ({
          designSlug: d.designSlug,
          view: failView,
          error: "design has no image data",
        })),
      });
    }
    // Resolve the output params: a selected preset wins, else the RESOLVED workspace output
    // (device-local override → tenant → global → default). These DRIVE the encode.
    const preset = presets.find((p) => p.name === presetName) ?? null;
    // FORMAT PRECEDENCE. Only an EXPLICIT choice may override the template's own
    // `render_settings.formats[0]`: an operator-picked preset, or a workspace format that
    // someone actually configured (device-local / tenant / global). The hard-coded fallback
    // ("default" provenance) is NOT a choice — it used to be truthy and therefore shadowed
    // every template's format, making per-template output formats dead code.
    const explicitFormat =
      preset?.format ??
      (resolvedSettings.output_format.source !== "default"
        ? resolvedSettings.output_format.value
        : undefined);
    const fmtFromSettings = explicitFormat && RENDER_FORMATS.has(explicitFormat)
      ? (explicitFormat as "jpg" | "png" | "webp")
      : null;
    const quality = preset?.quality ?? resolvedOutput.output_quality;
    // MAX CANVAS. The Settings hint promises "Largest output dimension allowed for custom
    // presets", but nothing on the client ever enforced it — an operator-authored 12000²
    // preset went straight to the engine. Clamp both dimensions here, at the single point
    // where the render encode is decided, so the promise is true for presets AND for a
    // hand-edited workspace width/height.
    //
    // The clamp scales BOTH axes by ONE factor. Clamping each axis independently
    // (`Math.min(w, cap)`, `Math.min(h, cap)`) turned a 6000×3000 preset into 3000×3000 and
    // STRETCHED the product — the exact distortion the square built-in presets exist to
    // avoid, introduced by the guard that was supposed to protect the render. It is
    // reachable: `PUT /api/settings` validates output_width/height against the ceiling but
    // not `output_presets`, and device-local overrides never reach the server at all.
    const maxCanvas = resolvedOutput.max_canvas_px;
    const wantW = preset?.width ?? resolvedOutput.output_width;
    const wantH = preset?.height ?? resolvedOutput.output_height;
    const scale = maxCanvas && maxCanvas > 0 ? Math.min(1, maxCanvas / Math.max(wantW, wantH)) : 1;
    // `max(1, …)` so a pathologically small ceiling can never produce a 0-pixel canvas.
    const width = scale < 1 ? Math.max(1, Math.round(wantW * scale)) : wantW;
    const height = scale < 1 ? Math.max(1, Math.round(wantH * scale)) : wantH;
    if (width !== wantW || height !== wantH) {
      logger?.breadcrumb("action", "Batch · output size capped to the max canvas", {
        maxCanvas, width, height, preset: preset?.name ?? null,
      });
      // …and SAY it. A silently-shrunk output is the class that must be loud: the operator
      // asked for one size and is getting another, and only a breadcrumb recorded it.
      setSizeCapped({ wantW, wantH, width, height, maxCanvas: maxCanvas! });
    } else {
      setSizeCapped(null);
    }
    // Spec 017 US6: resolve the approved brand's logo from the Select-tab pick. Resolved PER
    // TARGET (each selection carries its own `context.sellerBrandId`) — reading only
    // props.context, i.e. `batchSelections[0]`, dropped the logo from every other template in
    // a multi-template run. Fetch the brand list lazily — only when some target actually
    // chose a brand — and never let it block the render: any failure (or a brand that is
    // missing/unapproved) proceeds WITHOUT a logo and leaves a breadcrumb.
    const sellerBrandIds = [
      ...new Set(targets.map((t) => t.context.sellerBrandId).filter((id): id is string => !!id)),
    ];
    const logoByBrand = new Map<string, string | undefined>();
    setBrandLogoMissing(null);
    if (sellerBrandIds.length > 0) {
      // Silent-failure sweep: every branch below used to leave only an `action` breadcrumb, so a
      // run the operator set up WITH a brand exported a few hundred unbranded files and they
      // found out from the files. The run still proceeds (a logo is an overlay, not the
      // mock-up) — it just says so, in the `sizeCapped` voice and tone.
      try {
        const bs = await client.listBrands?.();
        for (const sellerBrandId of sellerBrandIds) {
          const brand = bs?.find((b) => b.id === sellerBrandId && (b.status ?? "approved") === "approved");
          logoByBrand.set(sellerBrandId, brand?.logo_ref ?? undefined);
          if (!brand) {
            logger?.log("warn", "Batch · seller brand not found or not approved — rendering without a logo", { step: "brandLogo", sellerBrandId });
            setBrandLogoMissing("the selected brand is not approved (or no longer exists)");
          } else if (!brand.logo_ref) {
            logger?.log("warn", "Batch · seller brand has no logo — rendering without a logo", { step: "brandLogo", sellerBrandId });
            setBrandLogoMissing("the selected brand has no logo uploaded");
          }
        }
      } catch (e) {
        logger?.log("error", "Batch · brand-logo lookup failed — rendering without a logo", {
          step: "brandLogo", sellerBrandIds, error: e,
        });
        setBrandLogoMissing(`the brand list could not be loaded (${e instanceof Error ? e.message : String(e)})`);
      }
    }
    // One batch id for the WHOLE run so the queue groups every template's job into a single
    // batch entry (templates × designs × views) instead of one row per template.
    //
    // review F-7: `nextRunId`, not a bare `Date.now()`. This id is the RUN KEY — the Queue's
    // grouping, the run's one `RunWriter` (its folder, its claim map, its single automatic
    // reveal) and the persisted job record all hang off it, so two runs sharing a millisecond
    // shared all of that.
    const runBatchId = nextRunId("run");
    // One UNIQUE path segment per template so a multi-template run can't overwrite itself.
    const segments = templateSegments(targets);
    for (const target of targets) {
      // Template's own saved output format (spec: `render_settings.formats[0]`). It ranks
      // BELOW an explicit preset/workspace choice and ABOVE the catalog display fallback.
      const templateFormat = target.templateVersion.payload?.render_settings?.formats?.[0];
      const format =
        fmtFromSettings ??
        (templateFormat && RENDER_FORMATS.has(templateFormat) ? templateFormat : target.context.ext);
      const designBatch: DesignBatch = {
        id: `${nextRunId("batch")}-${target.templateId}`,
        customerId: props.customerId,
        tenantId: props.tenantId,
        templateId: target.templateId,
        versionId: target.versionId,
        source: "upload",
        designs: renderableDesigns, // only tree-selected designs that carry image bytes
        views: target.views, // render THIS template's own saved views (not a hard-coded default)
        createdAt: Date.now(),
      };
      queue.enqueue({
        batch: designBatch,
        templateId: target.templateId,
        batchId: runBatchId,
        templateName: target.name,
        // WS-P / AUD-8 LB-7: carry the catalog row's "this version published a bake" bit into
        // the job, so a device missing the bake REFUSES instead of warping the design onto the
        // bare case. Already resolved by `selectionFromTemplate` — no fetch here.
        //
        // LOAD-BEARING, and the only production enqueue in the app: drop this field and the
        // whole wave is inert with every other gate green. Pinned by
        // `batch-panel.test.tsx` → "BatchPanel forwards bakeExpected to the queue (WS-P b-1)".
        bakeExpected: target.bakeExpected,
        settings: { format, quality, width, height },
        ctx: {
          customer: target.context.customer,
          product: target.context.product,
          // Export path segment → different templates land in different folders. Uses the
          // run-unique segment (name, or name + id when two selected templates share a name):
          // without it, two templates for the same product/brand/model wrote IDENTICAL paths
          // and silently overwrote each other's output.
          templateName: segments.get(target.templateId) ?? target.name,
          phoneSlug: target.context.phoneSlug,
          caseStyleSlug: target.context.caseStyleSlug,
          ext: format, // filename extension follows the chosen output format
          templateVersion: target.templateVersion,
          // Mockups export scheme — the Mockup Generator funnel's REAL labels win when it
          // drove this selection; otherwise they're derived from the context (buildRef also
          // falls back to product/designSlug if a field is still absent).
          productType: target.productType ?? mockupProductType(target.context.product),
          // F-7: the resolved brand, or NO brand segment — never one split off an id.
          brand: target.brand,
          model: target.model ?? mockupModel(target.context.phoneSlug),
          // Spec 017 US6: persist the approved brand's logo URL; the queue worker fetches
          // the bytes at render time and the engine merges it at the view's placement.
          brandLogoRef: target.context.sellerBrandId
            ? logoByBrand.get(target.context.sellerBrandId)
            : undefined,
        },
      });
    }
    // The run is now in the queue — reveal the hand-off to the live Queue tab. Set only
    // here (past every early return), so a save-folder cancel never surfaces it.
    setEnqueued(true);
    // Auto-navigate to the live Queue page so `Render` lands the operator where the
    // work is (progress/pause/cancel) instead of a dead click. Fires ONLY on a real enqueue
    // (past every early return incl. save-folder cancel); the `view-queue` button remains as
    // a way back. Absent in standalone/demo mounts that don't pass the hook.
    props.onViewQueue?.();
    } finally {
      // Single reset point for the click→enqueue-settled busy latch. `pending`/`rendering`
      // (both folded into `busy`) carry the button through the click→first-progress gap and
      // the render itself from here.
      setEnqueueing(false);
    }
  };

  const multi = batchSelections.length > 0;
  // The queue refuses a template whose declared mock-up is not on this device, per job and
  // only at run time. Checked here instead, so the fix is one click on this page.
  const missingMockups = useMissingMockups(targets);
  // The Templates find field. One predicate for both shapes of row — a queued
  // `TemplateSelection` and the single-selection fallback `BatchTarget` — because they are the
  // same row to the operator, and a filter that worked on one list and not the other would be
  // a find field that lies on the commonest path (the one-template flow).
  const matchesTemplateQuery = (row: { name: string; templateId: string }): boolean => {
    const q = templateQuery.trim().toLowerCase();
    if (!q) return true;
    return `${row.name} ${row.templateId}`.toLowerCase().includes(q);
  };
  // Total mockups = Σ per target (SELECTED designs × that template's own view count).
  const mockups = targets.reduce((sum, t) => sum + designCountSel * t.views.length, 0);
  // Why the Render button is disabled — surfaced as a title + a visible hint so a click on a
  // disabled button isn't a silent no-op (the root cause of "folder selection is not coming":
  // users were clicking a disabled button and never saw the save-folder dialog at all).
  const disabledReason = busy
    ? "A batch is already running"
    : designCountSel === 0
      ? "Select at least one design in the Designs list first"
      : targets.length === 0
        ? "Add a template to the batch first"
        : missingMockups.length > 0
          ? "Download the missing mock-ups first"
          : null;

  return (
    <section className="batch" data-testid="batch-panel">
      {/* R11 (Direction B): the Batch surface had no heading of its own — the tab label was
          doing the work of a title. `.panel-title` on an `<h2>`: the route's one `<h1>`
          ("Mock-ups") belongs to `app/ExportWorkspace.tsx`. */}
      <header className="batch__head">
        <h2 className="panel-title">Batch</h2>
      </header>

      {/* Rule 9: a banner names the tone it MEANS. `.banner` alone resolves to the danger
          tone, which is right here and wrong two banners down. */}
      {error && <p className="banner banner--danger" role="alert"><ErrorNotice detail={error} /></p>}

      {/* The run's output size was reduced to the workspace ceiling. Visible, not just a
          breadcrumb: the operator picked a size and is receiving a different one. A shrunk
          render still DELIVERED, so this is the warn tone, not the danger default. */}
      {sizeCapped && (
        <p className="banner banner--warn" role="status" data-testid="batch-size-capped">
          Output size reduced to this workspace’s {sizeCapped.maxCanvas}px canvas limit:{" "}
          {sizeCapped.wantW}×{sizeCapped.wantH} → {sizeCapped.width}×{sizeCapped.height}{" "}
          (aspect ratio kept). Raise “Max canvas” in Settings to render at full size.
        </p>
      )}

      {/* The run asked for a brand logo and is rendering without one. Warn, `role="status"`:
          the mock-ups still deliver — they just are not branded, and finding that out from a
          folder of exported files is the failure this replaces. */}
      {brandLogoMissing && (
        <p className="banner banner--warn" role="status" data-testid="batch-brand-logo-missing">
          Rendering without the seller brand’s logo — {brandLogoMissing}. The mock-ups will
          export unbranded. Fix the brand on the Select tab and render again if you need it.
        </p>
      )}

      <MissingMockups missing={missingMockups} testId="batch-missing-mockups" />

      {/* Whole-run failure/cancel banner — kept ABOVE the run so a swallowed engine throw or
          a save-folder cancel is unmissable instead of a silent "Rendering…" hang. Rule 9
          turns the full-width `.card` it used to be into a 720px banner object. */}
      {jobError && (
        <div className="banner banner--danger batch__job-error" role="alert" data-testid="batch-job-error">
          <span className="batch__job-error-msg"><ErrorNotice detail={jobError} /></span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setJobError(null)}
            data-testid="batch-job-error-dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Direction B: two dense PICK LISTS on the left, the run's own settings in an
          `.inspector` rail on the right. The settings used to be a `.card` wedged between the
          lists and the button row, which is why the button row had grown five ghost verbs and
          four settings controls in one wrap. */}
      <div className="batch__layout">
        <div className="batch__grid">
          {/* Templates — the batch targets as rows (multi = removable selections). */}
          <div className="card card--flush batch__col" data-testid="batch-templates">
            <div className="batch__col-head">
              <span className="batch__col-title">Templates</span>
              {multi ? (
                <button type="button" className="batch__col-action" onClick={clearBatch} data-testid="batch-clear-templates">
                  Clear all
                </button>
              ) : (
                <span className="batch__col-count">{targets.length}</span>
              )}
            </div>
            <div className="batch__col-tools">
              <input
                type="search"
                className="input batch__search"
                placeholder="Find a template…"
                value={templateQuery}
                onChange={(e) => setTemplateQuery(e.target.value)}
                data-testid="batch-template-search"
                aria-label="Find a template in this batch"
              />
            </div>
            <div className="panel-scroll">
              <ul className="batch__rows">
                {multi
                  ? batchSelections.filter(matchesTemplateQuery).map((s) => (
                      <li key={s.templateId} className="batch__row" data-testid="batch-template-chip">
                        <span className="batch__check batch__check--on" aria-hidden="true"><Icon name="check" size={16} /></span>
                        <span className="batch__row-meta">
                          <b className="batch__row-name">{s.name}</b>
                          <small className="batch__row-sub" title={s.templateId}>{chipSubtitle(s.context)}</small>
                        </span>
                        <button
                          type="button"
                          className="batch__row-x"
                          aria-label={`Remove ${s.name}`}
                          onClick={() => removeFromBatch(s.templateId)}
                          data-testid="batch-template-remove"
                        >
                          <Icon name="close" size={16} />
                        </button>
                      </li>
                    ))
                  : targets.filter(matchesTemplateQuery).map((t) => (
                      <li key={t.templateId} className="batch__row">
                        <span className="batch__check batch__check--on" aria-hidden="true"><Icon name="check" size={16} /></span>
                        <span className="batch__row-meta">
                          <b className="batch__row-name">{t.name}</b>
                          <small className="batch__row-sub" title={t.templateId}>{mockupProductType(t.context.product)}</small>
                        </span>
                      </li>
                    ))}
              </ul>
            </div>
          </div>

          {/* Designs — the imported artwork as a directory TREE; tick files/folders to render. */}
          <div className="card card--flush batch__col" data-testid="batch-designs">
            <div className="batch__col-head">
              <span className="batch__col-title">Designs</span>
              <span className="batch__col-count">{designCountSel} of {designCount} selected</span>
              <button type="button" className="batch__col-action" onClick={upload} data-testid="batch-upload">+ Add</button>
            </div>
            {importProgress && (
              <div className="gen-progress" data-testid="import-progress" role="status" aria-live="polite">
                <div className="gen-progress__head">
                  <span>Importing designs… {importProgress.done.toLocaleString()} / {importProgress.total.toLocaleString()}</span>
                </div>
                <div className="gen-progress__track"><div className="gen-progress__bar" /></div>
              </div>
            )}
            {designCount === 0 ? (
              <div className="batch__col-empty">
                <span>No designs yet.</span>
                <div className="batch__col-empty-actions">
                  <button type="button" className="btn btn--sm" onClick={upload}>Upload designs</button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={uploadFolder} data-testid="batch-upload-folder"
                          disabled={importing} aria-busy={importing}>Import folder</button>
                </div>
              </div>
            ) : (
              <>
                <div className="batch__col-tools">
                  <input
                    type="search"
                    className="input batch__search"
                    placeholder="Search designs…"
                    value={designQuery}
                    onChange={(e) => setDesignQuery(e.target.value)}
                    data-testid="batch-design-search"
                    aria-label="Search designs by name"
                  />
                </div>
                <div className="panel-scroll">
                  <DesignTree
                    designs={batch.designs}
                    selected={selectedSet}
                    onToggle={onTreeToggle}
                    onRemove={onTreeRemove}
                    filter={designQuery}
                  />
                </div>
                <div className="batch__col-foot">
                  <button type="button" className="btn btn--sm btn--ghost" onClick={uploadFolder} data-testid="batch-upload-folder"
                          disabled={importing} aria-busy={importing}>Import folder</button>
                </div>
              </>
            )}
          </div>

          {/* ST-18: the per-view "Background" card used to live here. It was ENTIRELY INERT —
              its Global/per-view choices were never read by `enqueue()`, and there is nowhere
              to send them: the engine's run-time `RenderSettings` has no background input
              (format/quality/dpi/width/height only). The real, working control is the
              template's own `render_settings.background`, edited in the template editor and
              baked into the version the render already uses. A picker that quietly does
              nothing is worse than no picker, so it is removed rather than left as decoration.
              Re-add it here only together with an engine input that actually consumes it. */}
        </div>

        {/* Rule 8: "This run" — everything the next Render will use, in one rail. This was
            `.batch__summary`, a 24px-padded `.card` that also held the button row. */}
        <aside className="inspector batch__run" data-testid="batch-run-inspector">
          <div className="inspector__head">
            <span>This run</span>
            <span data-testid="batch-run-total">{mockups} mock-up{mockups === 1 ? "" : "s"}</span>
          </div>
          <div className="inspector__body">
            {/* Output preset (moved from the deleted options card) — drives the render encode. */}
            {presets.length > 0 && (
              <label className="batch__opt" data-testid="batch-preset">
                <span className="batch__opt-label">Output preset</span>
                <select value={presetName} onChange={(e) => setPresetName(e.target.value)} data-testid="batch-preset-select">
                  <option value="">Workspace default</option>
                  {presets.map((p) => (
                    <option key={p.name} value={p.name}>
                      {p.name} — {p.format.toUpperCase()} {p.width}×{p.height} q{p.quality}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {/* Save folder (desktop only) — the operator-chosen destination stays VISIBLE for
                the whole session with a re-pick control. */}
            {io.kind === "desktop" && (
              <div className="batch__savedir" data-testid="batch-savedir">
                <span className="batch__savedir-label">Save to folder</span>
                <span
                  className="batch__savedir-path faint"
                  data-testid="batch-savedir-path"
                  title={saveDir ?? undefined}
                >
                  {saveDir ?? "Not chosen yet — you'll be asked when you create mock-ups"}
                </span>
                {/* WS-O / LB-2 (1): an unusable folder is NAMED here, when it is picked, rather
                    than discovered at the end of an unattended seven-minute run. A healthy folder
                    shows nothing — a badge that is always there is a badge nobody reads. */}
                {saveDirHealthLabel(saveDirHealth) && (
                  <span
                    className={saveDirHealthLabel(saveDirHealth)!.cls}
                    data-testid="batch-savedir-health"
                  >
                    {saveDirHealthLabel(saveDirHealth)!.label}
                  </span>
                )}
                {/* The owner: "Give an option to directly open the folder — if I click the
                    folder image, or there should be an option called Open folder." The action
                    bar's control (below) says the word but only exists once a run has written
                    something; THIS row names the destination all session long and had no way to
                    go there. Rule 13 shape — an icon, its name in a tooltip, no word in the row.

                    `openTarget` is the RUN folder once there is one and the chosen folder before
                    that: mid-run "the folder" means the dated subfolder that is filling, and
                    before a run it means the folder the operator just picked. */}
                {io.openPath && openTarget && (
                  <RowAction
                    label="Open folder"
                    icon="folder"
                    tone="quiet"
                    onClick={() => openFolder(openTarget)}
                    data-testid="batch-savedir-open"
                  />
                )}
                <BusyButton
                  className="btn btn--ghost btn--sm"
                  onAction={chooseSaveDir}
                  data-testid="batch-savedir-choose"
                >
                  {saveDir ? "Change…" : "Choose…"}
                </BusyButton>
              </div>
            )}
            {io.kind === "desktop" && saveDirHealthNote(saveDirHealth) && (
              <small className="faint" role="status" data-testid="batch-savedir-health-note">
                {saveDirHealthNote(saveDirHealth)}
              </small>
            )}
            {/* WS-H1 / D-13 (AUD-3 LB-4): a re-render used to silently replace the previous run's
                files. Each run now writes its own dated folder unless the operator opts out. */}
            {/* Fix round 1, F-4: `--check`, because a tick and its label are a ROW. Every other
                `.batch__opt` is a label ABOVE a control, which is why the base rule is a column
                — and a bare checkbox in a column flex parent stretched to the rail's full width
                (267px at 1280, ~700px at 1024) with the glyph drawn in the middle of it. */}
            <label className="batch__opt batch__opt--check" data-testid="batch-replace-run">
              <input
                type="checkbox"
                checked={replacePreviousRun}
                onChange={(e) => useBatchStore.getState().setReplacePreviousRun(e.target.checked)}
                data-testid="batch-replace-run-input"
              />
              <span>Replace previous run</span>
              <small className="faint">
                {/* G-22: the root is a PATH on disk, not chrome copy — so it is read from the
                    constant that creates it (`export/bundle.ts`) instead of being spelled again
                    here. The example can no longer drift from the folder the run actually writes,
                    and the one-spelling sweep stops reading an on-disk name as prose. */}
                Off: each run writes its own dated folder (<code>{MOCKUPS_BUNDLE_ROOT}/2026-09-11 14-32/</code>).
                On: every run writes straight into <code>{MOCKUPS_BUNDLE_ROOT}/</code> and overwrites the last one.
              </small>
            </label>
            {/* WS-H1 / D-10 (AUD-3 LB-7): the folder name implied a DPI-scaled render of the print
                area. WS-H2 wired it (`genz_render_print_file` at `render_settings.print_dpi`), but
                only templates that are a valid print source can produce one — and on web there is
                no native renderer at all. Two states, one testid: the note says which one this run
                got rather than letting the folder name be discovered at the print shop.

                review-1 M-2: the empty state says "no rendered print files in this run" and NOT
                "this template is not a print-file source". All this branch knows is that the store
                holds none — which is equally true before any run has started, and on a multi-
                template run where one of several templates rendered nothing. Naming the template as
                the cause was a conclusion the data does not support. */}
            <small className="faint" data-testid="batch-print-note">
              {!exportsPrintFiles ? (
                // WS-Y: the run writes the mock-ups. Naming a folder it does not create is the one
                // thing this note has always existed to prevent, so with print files off it says
                // what IS exported instead of describing a folder that will not be there.
                <>This run exports the mock-ups only — print files are off for now.</>
              ) : batch.printFiles.length > 0 ? (
                <>
                  Print files (named “… - Print”) sit beside the mock-ups in each model folder and
                  hold the print area rendered at the template&apos;s print DPI.
                </>
              ) : (
                <>
                  Print files (named “… - Print”) sit beside the mock-ups in each model folder and
                  hold each design&apos;s original artwork file — not a DPI-scaled render of the
                  print area (no rendered print files in this run).
                </>
              )}
            </small>
            {/* WS-O review-1 B-1: the run's own refusals, in the rail that describes the run.
                `role="status"` and never a toast: this is the run's outcome, and it has to
                survive the next status message rather than be overwritten by it. */}
            {runProblemNotice(runReport) && (
              <span
                className={runProblemNotice(runReport)!.cls}
                role="status"
                // review-2 F-1: the notice wraps (`badge--wrap`), so nothing is hidden; the title
                // is the belt to that braces for any rail narrower than this sentence.
                // Fix round 1, F-3: `badge--wrap` is no longer a pill. This is the one badge in
                // the app whose text is a sentence, and in a 274px rail it wraps to four lines —
                // a stadium cap of ~39px put the tone dot outside the painted shape.
                title={runProblemNotice(runReport)!.text}
                data-testid="batch-run-problems"
              >
                {runProblemNotice(runReport)!.text}
              </span>
            )}
            {/* WS-O / LB-2: a failed reveal is an INLINE row with the path and a Retry — not a
                parenthetical appended to a status line that the next message overwrites.
                Mirrors `QueuePanel`'s `batch-details-open-error`. */}
            {openErr && lastSavedPath && (
              <div className="batch__open-error" role="alert" data-testid="batch-open-error">
                <span><ErrorNotice detail={openErr} /></span>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setOpenErr(null);
                    void io.openPath?.(lastSavedPath).catch((e: unknown) =>
                      setOpenErr(e instanceof Error ? e.message : String(e)));
                  }}
                  data-testid="batch-open-error-retry"
                >
                  Retry
                </button>
              </div>
            )}
            {saveMsg && <p className="faint" role="status" data-testid="save-msg">{saveMsg}</p>}
            {ftpMsg && <p className="faint" role="status" data-testid="ftp-msg">{ftpMsg}</p>}
          </div>
        </aside>
      </div>

      {/* Rule 7: the page's commit, at the bottom of the working column. The count leads the
          bar (it is what the click is about), the five post-run verbs follow, and `ActionBar`
          places the ONE primary last — and REPORTS a second in DEV (a `console.error`, once per
          bar). It deliberately never throws: fix round 1, F-5 in `ui/ActionBar.tsx` says why a
          layout primitive may not take a route out. The enforcement is the measured `B4` and
          the per-state primary counts in this panel's suite — not this bar's own guard. */}
      <ActionBar label="Run controls" primary={
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void enqueue()}
          disabled={disabledReason !== null}
          aria-busy={busy}
          title={disabledReason ?? undefined}
          data-testid="enqueue-render"
        >
          {busy ? RENDER_BUSY_LABEL : RENDER_LABEL}
        </button>
      }>
        {/* The run's size, said once. It used to be said twice — here and again inside the
            primary's own label ("Create Mock-up (2 templates × 3 designs)"), which is why the
            button was the widest control on the screen. */}
        <span className="batch__count" data-testid="batch-summary-count">
          <b>{targets.length} template{targets.length === 1 ? "" : "s"} × {designCountSel} design{designCountSel === 1 ? "" : "s"}</b>
          {" · "}{mockups} mock-up{mockups === 1 ? "" : "s"} · placement comes from each template
        </span>
        {jobCount > 1 && (
          <span className="faint batch__queue-hint" data-testid="batch-queue-hint">
            → 1 batch ({targets.length} templates) in the queue
          </span>
        )}
        {disabledReason !== null && !busy && (
          <span className="faint" data-testid="render-disabled-hint">{disabledReason}</span>
        )}
        {/* WS-O / AUD-8 LB-2: the post-run action the Batch panel never had. Enabled on
            `lastSavedPath`, NOT on a write count — a run whose writes were all refused is
            exactly when the operator needs to see the (empty) folder and the reason. Same
            string as the Queue batch-details control, so the operator learns it once. */}
        {io.openPath && lastSavedPath && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => openFolder(lastSavedPath)}
            data-testid="batch-open-folder"
          >
            Open folder
          </button>
        )}
        {/* WS-O / O-8 (WS-N review-1 M-4): WEB has no folder, so "each file lands as it
            finishes" is not achievable and this does not pretend otherwise. The run's zip is
            accumulated as it goes, the count climbs LIVE so the operator can watch it fill,
            and the click is the gesture that hands the bytes to the browser. */}
        {io.kind !== "desktop" && (webZip?.count ?? 0) > 0 && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void downloadWebZip()}
            data-testid="batch-download-zip"
          >
            Download mock-ups ({webZip!.count})
          </button>
        )}
        {/* WS-O / LB-3: verb two of two. "Save to folder" / "Download mock-ups" is gone — the
            run writer already wrote every mockup into the chosen folder as it finished, so
            the only honest re-export is a SECOND destination. */}
        {io.kind === "desktop" && io.pickSaveDirectory && lastSavedPath && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void copyRunToFolder()}
            data-testid="batch-copy-run"
          >
            Copy run to…
          </button>
        )}
        {io.kind === "desktop" && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void exportFtp()}
            disabled={ftpBusy || batch.outputs.length === 0}
            aria-busy={ftpBusy}
            data-testid="export-ftp"
          >
            {ftpBusy ? (
              <>Uploading…<span className="btn-spinner" aria-hidden="true" /></>
            ) : (
              "Export to server"
            )}
          </button>
        )}
        {/* Queue hand-off — once a run is enqueued, point the operator at the live Queue
            tab (progress/pause/cancel) so "Render" isn't a dead click. */}
        {enqueued && props.onViewQueue && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => props.onViewQueue?.()}
            data-testid="view-queue"
          >
            View progress in Queue
          </button>
        )}
        {/* WS-O: the "Saving mockups to your folder…" row is gone with the end-of-run write it
            described. There is no post-render gap left to fill — the files land as the run's own
            progress bar advances, which is the only write progress there now is. */}
      </ActionBar>

      <Gallery outputs={batch.outputs} progress={batch.progress} resolveBytes={resolveSpilledBytes}
        run={batch.runStartedAt === undefined ? undefined : { startedAt: batch.runStartedAt, endedAt: batch.runEndedAt }} />
    </section>
  );
}
